/**
 * Les numéros manquants À L'INTÉRIEUR de la plage réellement émise.
 *
 * Sert à PROPOSER une récupération de trous anciens — ceux que le registre
 * `released_document_numbers` ne connaît pas parce qu'ils datent d'avant lui,
 * ou d'une suppression faite hors de l'application (SQL direct, import).
 *
 * ⚠ Ce scan ne décide de rien tout seul, et ne doit jamais être branché
 * directement sur l'attribution des numéros. C'est exactement ce qu'on faisait
 * avant, et ça avait fait émettre un BLC-0001 en production sur une série
 * importée démarrant à 183418 : un trou CONSTATÉ ne prouve pas qu'un document a
 * existé. Ici le résultat est soumis à l'utilisateur, qui seul sait si le n°
 * 2600149 est une facture qu'il a supprimée ou un numéro que son ancien
 * logiciel n'a jamais émis.
 *
 * Trois garde-fous malgré tout, pour que la proposition reste raisonnable :
 *  - la plage s'arrête aux bornes de la série vivante (`from`/`to`). Rien sous
 *    le plus petit numéro existant n'est proposé : sur une série importée
 *    démarrant à 183418, les 183417 valeurs du dessous ne sortent jamais ;
 *  - les LONGUES plages absentes sont écartées (`maxRunLength`) : voir plus bas ;
 *  - le résultat est plafonné (`limit`), `total` disant l'ampleur réelle. Une
 *    reprise partielle d'historique peut ouvrir des millions de trous, et une
 *    liste illimitée noierait l'écran comme la requête de récupération.
 */

/**
 * Au-delà de combien de numéros consécutifs absents une plage cesse d'être une
 * série de suppressions pour devenir une FRONTIÈRE DE SÉRIE.
 *
 * Le cas qui a motivé ce seuil (tenant Tunielec, constaté le 2026-08-18) : le
 * client encode l'année dans le numéro lui-même (`FAC-2400231`, `FAC-2600164`)
 * tout en ayant `doc_number_include_year = false`. L'app voit donc UNE série
 * continue de 2400015 à 2600164, dans laquelle les intervalles entre deux
 * millésimes — 2400231→2500000 et 2500327→2600000 — apparaissent comme des
 * trous. Résultat sans ce filtre : 199 620 « trous » proposés, dont les 500
 * premiers tous situés dans le vide de 2024, et le seul trou réel (2600149)
 * relégué au rang 199 619, donc invisible. L'écran devenait non seulement
 * inutile mais dangereux : « Tout cocher » puis « Récupérer » aurait fait
 * porter FAC-2400016 à la facture suivante — la régression BLC-0001 rejouée,
 * cette fois par la main de l'utilisateur.
 *
 * La mesure sur ces données réelles est franchement bimodale : les deux vides
 * inter-millésimes font 99 770 et 99 674 numéros, tandis que la PLUS LONGUE
 * plage légitime en fait 18. Quatre ordres de grandeur séparent les deux
 * familles ; 100 se pose au milieu avec une marge confortable des deux côtés.
 *
 * Passer `Infinity` désactive le filtre (utile pour tester le comportement brut).
 */
export const MAX_RECLAIMABLE_RUN = 100;

export interface SeriesGaps {
  /** Les numéros manquants, croissants, tronqués à `limit`. */
  values: number[];
  /** Combien il en existe en tout, troncature ignorée. */
  total: number;
  /** `true` si `values` ne montre qu'une partie de `total`. */
  truncated: boolean;
  /** Plus petit numéro existant — borne basse du scan. `null` si série vide. */
  from: number | null;
  /** Plus grand numéro existant — borne haute du scan. `null` si série vide. */
  to: number | null;
  /** Combien de plages ont été écartées comme frontières de série. */
  skippedRuns: number;
  /**
   * Combien de numéros ces plages représentent. Écarter 199 444 numéros sans le
   * dire ferait passer une troncature massive pour une série propre — l'écran
   * doit pouvoir l'annoncer.
   */
  skippedValues: number;
}

export function findSeriesGaps(
  existingNumbers: number[],
  limit = 500,
  maxRunLength: number = MAX_RECLAIMABLE_RUN,
): SeriesGaps {
  const sorted = [...new Set(existingNumbers.filter((n) => Number.isInteger(n) && n >= 1))].sort(
    (a, b) => a - b,
  );

  const from = sorted.length === 0 ? null : sorted[0]!;
  const to = sorted.length === 0 ? null : sorted[sorted.length - 1]!;
  const safeLimit = Math.max(0, Math.trunc(limit) || 0);
  // `Math.trunc(Infinity)` vaut Infinity : le filtre se désactive proprement.
  const safeRun = Math.max(0, Math.trunc(maxRunLength) || 0);

  const values: number[] = [];
  let total = 0;
  let skippedRuns = 0;
  let skippedValues = 0;

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]!;
    const current = sorted[i]!;
    const gapSize = current - prev - 1;
    if (gapSize <= 0) continue;

    // Frontière de série : ni proposée, ni comptée dans `total`. La retirer de
    // `total` est le but même du filtre — c'est ce total que l'écran affiche,
    // et « 199 619 trous » sur une série qui n'en a que 176 de réels décrit un
    // problème qui n'existe pas.
    if (gapSize > safeRun) {
      skippedRuns++;
      skippedValues += gapSize;
      continue;
    }

    // `total` s'additionne par arithmétique, jamais en parcourant le trou : un
    // trou de 2,6 millions de valeurs se compte ici sans rien matérialiser.
    total += gapSize;
    for (let value = prev + 1; value < current && values.length < safeLimit; value++) {
      values.push(value);
    }
  }

  return { values, total, truncated: values.length < total, from, to, skippedRuns, skippedValues };
}
